import * as Sentry from "@sentry/react";
import { i18n } from "@lingui/core";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { Button, type ButtonProps, Divider } from "@heroui/react";
import { useMediaQuery } from "@mantine/hooks";
import dayjs from "dayjs";
import { capitalize } from "lodash";
import { CalendarCog } from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { LimeDrawer } from "@/Components/NextBase/LimeDrawer";
import { LimeStepperNumberInput } from "@/Components/NextBase/LimeStepperNumberInput";
import { LimeSelect } from "@/Components/NextBase/LimeSelect";
import { LimeServiceSelect } from "@/Components/NextBase/LimeServiceSelect";
import { LimeSwitch } from "@/Components/NextBase/LimeSwitch";
import { ExportPdfModal } from "@/Components/ExportPdfModal/ExportPdfModal";
import { useClientFeature } from "@/hooks/useClientFeature";
import { OptionSelect } from "./OptionSelect";
import { useCalendarFilterOptions } from "./useCalendarFilterOptions";
import {
  CALENDAR_FILTER_DAY_IDS,
  type CalendarFilterMode,
  type CalendarFilterOption,
  type CalendarFilterValue,
} from "./types";

type CalendarFilterProps = {
  value: CalendarFilterValue;
  onSave: (value: CalendarFilterValue) => void | Promise<void>;
  locationId: number | null;
  onLocationChange: (locationId: number) => void;
  showCalendarMode?: boolean;
  showCalendarSettings?: boolean;
  /** Needs the `fixedAppointments` feature; ignored without it. */
  showFixedAppointmentsToggle?: boolean;
  showExportPdf?: boolean;
  triggerIcon?: ReactNode;
  triggerProps?: ButtonProps;
};

export function CalendarFilter({
  value,
  onSave,
  locationId,
  onLocationChange,
  showCalendarMode = false,
  showCalendarSettings = false,
  showFixedAppointmentsToggle = false,
  showExportPdf = false,
  triggerIcon,
  triggerProps,
}: CalendarFilterProps) {
  const { isFeatureEnabled } = useClientFeature();
  const isDesktop = useMediaQuery("(min-width: 768px)");

  const { locations, users, services, resources, areOptionsReady } =
    useCalendarFilterOptions(locationId);

  const [isOpen, setIsOpen] = useState(false);
  // The draft is seeded on open and only read while the drawer is open, so an
  // external change to `value` can never clobber in-progress edits.
  const [draft, setDraft] = useState<CalendarFilterValue>(value);
  const [isSaving, setIsSaving] = useState(false);

  // Picking a location re-keys the option queries, so the draft is re-seeded
  // once `value` describes that location and its options have landed.
  useEffect(() => {
    if (!areOptionsReady) return;
    if (draft.locationId === locationId) return;
    if (value.locationId !== locationId) return;
    setDraft(value);
  }, [areOptionsReady, draft.locationId, locationId, value]);

  const areResourcesEnabled = isFeatureEnabled("resources");

  // Memoized rather than module-cached: a fresh array every render rebuilds the
  // select's collection mid-animation and flickers the popover, but caching for
  // the session would freeze in whatever dayjs produced the very first time.
  const days = useMemo(() => getWeekdayOptions(i18n.locale), [i18n.locale]);

  function patch(next: Partial<CalendarFilterValue>) {
    setDraft((prev) => ({ ...prev, ...next }));
  }

  // Each mode renders its own columns, so only that mode's list may not be
  // emptied; a filter with no mode (fixed appointments) requires neither.
  const isUsersRequired = draft.calendarMode === "users" && users.length > 0;
  const isResourcesRequired =
    areResourcesEnabled &&
    draft.calendarMode === "resources" &&
    resources.length > 0;
  const isServicesRequired = services.length > 0;
  const allowedServiceIds = useMemo(
    () => new Set(services.map((service) => service.id)),
    [services],
  );
  const locationItems = useMemo(
    () =>
      locations.map((location) => ({
        key: location.id.toString(),
        label: location.label,
      })),
    [locations],
  );

  const hasUsers = draft.users.length > 0;
  const hasResources = draft.resources.length > 0;
  const hasServices = draft.services.length > 0;
  const hasDays = draft.days.length > 0;

  const canSave =
    (!isUsersRequired || hasUsers) &&
    (!isResourcesRequired || hasResources) &&
    (!isServicesRequired || hasServices) &&
    hasDays;

  function openFilter() {
    setDraft(value);
    setIsOpen(true);
  }

  function closeFilter() {
    setIsOpen(false);
  }

  async function saveFilter() {
    setIsSaving(true);
    try {
      await onSave(draft);
      setIsOpen(false);
    } catch (error) {
      // Left open so the edit survives and can be retried.
      Sentry.captureException(error);
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div>
      <Button
        onPress={openFilter}
        isIconOnly
        variant="flat"
        aria-label={t`Filter`}
        isDisabled={!areOptionsReady}
        data-identifier="calendar-filter-trigger"
        {...triggerProps}
      >
        {triggerIcon ?? <CalendarCog size={16} />}
      </Button>

      <LimeDrawer
        drawerProps={{
          isOpen,
          onClose: closeFilter,
          placement: isDesktop ? "right" : "bottom",
          dataIdentifier: "calendar-filter-drawer",
        }}
        header={{ closeButtonRowContent: t`Filter` }}
        body={{
          content: (
            <div className="flex flex-col gap-4">
              <LimeSelect
                data-identifier="calendar-filter-location"
                items={locationItems}
                value={locationId?.toString()}
                onSingleItemChange={(key) => {
                  if (!key) return;
                  onLocationChange(Number(key));
                }}
                placeholder={t`Lokacija`}
                label={t`Lokacija`}
                variant="bordered"
                popoverProps={{ disableAnimation: true }}
              />

              {showCalendarMode && areResourcesEnabled && (
                <LimeSelect
                  data-identifier="calendar-filter-mode"
                  items={[
                    {
                      label: t`Zaposleni`,
                      key: "users",
                      identifier: "calendar-filter-mode-users",
                    },
                    {
                      label: t`Sredstva`,
                      key: "resources",
                      identifier: "calendar-filter-mode-resources",
                    },
                  ]}
                  value={draft.calendarMode}
                  onSingleItemChange={(value) =>
                    patch({ calendarMode: value as CalendarFilterMode })
                  }
                  variant="bordered"
                  label={t`Pogled koledarja`}
                  disallowEmptySelection={true}
                  popoverProps={{ disableAnimation: true }}
                />
              )}

              {users.length > 0 && (
                <OptionSelect
                  label={t`Zaposleni`}
                  allLabel={t`Vsi zaposleni`}
                  emptyLabel={t`Ni izbranih zaposlenih`}
                  dataIdentifier="calendar-filter-users"
                  options={users}
                  selected={draft.users}
                  onChange={(ids) => patch({ users: ids })}
                  error={
                    isUsersRequired && !hasUsers
                      ? t`Izbrati morate vsaj 1 zaposlenega`
                      : undefined
                  }
                />
              )}

              {areResourcesEnabled && resources.length > 0 && (
                <OptionSelect
                  label={t`Sredstva`}
                  allLabel={t`Vsa sredstva`}
                  emptyLabel={t`Ni izbranih sredstev`}
                  dataIdentifier="calendar-filter-resources"
                  options={resources}
                  selected={draft.resources}
                  onChange={(ids) => patch({ resources: ids })}
                  error={
                    isResourcesRequired && !hasResources
                      ? t`Izbrati morate vsaj 1 sredstvo`
                      : undefined
                  }
                />
              )}

              {services.length > 0 && (
                <LimeServiceSelect
                  variant="bordered"
                  data-identifier="calendar-filter-services"
                  placeholder={t`Ni izbranih storitev`}
                  value={draft.services}
                  onChange={(ids) => patch({ services: ids })}
                  filterService={(service) =>
                    allowedServiceIds.has(service.serviceId)
                  }
                  popoverProps={{ disableAnimation: true }}
                  error={
                    isServicesRequired && !hasServices
                      ? t`Izbrati morate vsaj 1 storitev`
                      : undefined
                  }
                />
              )}

              <OptionSelect
                label={t`Prikazani dnevi`}
                allLabel={t`Vsi dnevi`}
                emptyLabel={t`Ni izbranih dni`}
                dataIdentifier="calendar-filter-days"
                options={days}
                selected={draft.days}
                onChange={(ids) => patch({ days: ids })}
                isSearchable={false}
                error={hasDays ? undefined : t`Izbrati morate vsaj 1 dan`}
              />

              {showCalendarSettings && (
                <CalendarSettings
                  value={draft}
                  onChange={patch}
                  showFixedAppointmentsToggle={
                    showFixedAppointmentsToggle &&
                    isFeatureEnabled("fixedAppointments")
                  }
                />
              )}

              {showExportPdf && <ExportPdfModal />}
            </div>
          ),
        }}
        footer={{
          content: (
            <>
              <Button
                onPress={closeFilter}
                variant="flat"
                data-identifier="calendar-filter-cancel"
              >
                <Trans>Prekliči</Trans>
              </Button>
              <Button
                // `areOptionsReady` gates it too: mid-switch the draft still
                // holds the previous location's ids, and saving would persist
                // them against the new one (DEV-8678).
                isDisabled={!canSave || !areOptionsReady || isSaving}
                isLoading={isSaving}
                onPress={() => void saveFilter()}
                color="primary"
                data-identifier="calendar-filter-save"
              >
                <Trans>Shrani</Trans>
              </Button>
            </>
          ),
        }}
      />
    </div>
  );
}

type CalendarSettingsProps = {
  value: CalendarFilterValue;
  onChange: (next: Partial<CalendarFilterValue>) => void;
  showFixedAppointmentsToggle: boolean;
};

function CalendarSettings({
  value,
  onChange,
  showFixedAppointmentsToggle,
}: CalendarSettingsProps) {
  return (
    <div className="flex flex-col gap-4">
      <Divider className="mt-1" />

      <p className="font-medium text-default-500 text-small">
        <Trans>Nastavitve koledarja</Trans>
      </p>

      {/* The stepper's root is `h-full`, so in this flex column it stretches to
          swallow the gap below it; force it back to its content height. */}
      <div className="[&>div]:h-auto">
        <LimeStepperNumberInput
          value={value.timeStep}
          label={t`Časovni korak`}
          disabled={false}
          onValueChange={(timeStep: number) => onChange({ timeStep })}
          steppersProps={{ variant: "flat", size: "sm" }}
          size="sm"
          step={5}
          min={5}
          max={300}
          variant="flat"
          endContent={<span className="text-sm">{t`min`}</span>}
        />
      </div>

      <LimeSwitch
        value={!!value.showOnlyUsersWithShifts}
        onValueChange={(showOnlyUsersWithShifts: boolean) =>
          onChange({ showOnlyUsersWithShifts })
        }
        title={t`Prikaži samo zaposlene z delovnim časom`}
        description={t`Za vsak dan prikaži le zaposlene, ki imajo delovni čas.`}
        isCompact
      />

      <LimeSwitch
        value={!!value.showStaffNames}
        onValueChange={(showStaffNames: boolean) =>
          onChange({ showStaffNames })
        }
        title={t`Prikaži imena zaposlenih in sredstev`}
        description={t`Pod sliko zaposlenega ali sredstva na koledarju prikaži njegovo ime.`}
        isCompact
        data-identifier="calendar-filter-show-staff-names"
      />

      {showFixedAppointmentsToggle && (
        <LimeSwitch
          value={!!value.showOnlyFixedAppointments}
          onValueChange={(showOnlyFixedAppointments: boolean) =>
            onChange({ showOnlyFixedAppointments })
          }
          title={t`Prikaži samo redne termine`}
          description={t`Na koledarju prikaži le redne termine.`}
          isCompact
        />
      )}
    </div>
  );
}

function getWeekdayOptions(locale: string): CalendarFilterOption[] {
  const days = CALENDAR_FILTER_DAY_IDS.map((offset) => {
    const day = dayjs().locale(locale).startOf("week").add(offset, "day");
    return { id: day.day(), label: capitalize(day.format("dddd")) };
  });

  // Locales starting the week on Sunday would otherwise list it first.
  if (days[0].id === 0) {
    days.push(days.shift()!);
  }

  return days;
}
